/* eslint-disable no-console, no-plusplus, prefer-arrow-callback, no-await-in-loop */
import html2canvas from 'html2canvas';
import JsPDF from 'jspdf';
import { PDF_EXPORT_QUERY_PARAM_EXPORT_TARGET } from 'common/constants/pdfExport';
import { renderCoverTemplate, renderPageTemplate } from './templates';
import {
  CONVERSION_RATIOS,
  IMAGE_SETTINGS,
  MultiplePagesExportOptions,
  MultiplePagesExportResult,
  PROGRESS_MILESTONES,
  PROGRESS_PERCENTAGE,
  RECENT_REQUEST_THRESHOLD_MS,
  SmoothProgressAnimator,
  TEMPLATE_CONSTANTS,
  TIMEOUT_MS,
} from './types';
import { Logger } from '../../../utilities';

/**
 * Check if there are any pending network requests using Performance API
 */
const hasPendingNetworkRequests = (): boolean => {
  try {
    // Use Performance API to check for pending requests
    const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];

    // Check if any resource has responseEnd === 0 (indicating pending request)
    const hasPendingResources = resources.some(resource => resource.responseEnd === 0);

    if (hasPendingResources) {
      Logger.info('Found pending network requests via Performance API');
      return true;
    }

    // Additional check: look for recent requests that might still be processing
    const recentRequests = resources.filter(resource => {
      const now = performance.now();
      const requestTime = resource.startTime;
      const duration = now - requestTime;

      // If request started less than 5 seconds ago and responseEnd is 0, it might still be pending
      return duration < RECENT_REQUEST_THRESHOLD_MS && resource.responseEnd === 0;
    });

    if (recentRequests.length > 0) {
      Logger.info(`Found ${recentRequests.length} recent requests that might still be pending`);
      return true;
    }

    return false;
  } catch (error) {
    Logger.warn('Error checking pending requests via Performance API:', error);
    return false;
  }
};

/**
 * Monitor fetch and XMLHttpRequest calls to track pending requests
 */
const setupNetworkMonitoring = (): { hasPendingRequests: () => boolean; cleanup: () => void } => {
  let pendingFetchRequests = 0;
  let pendingXHRRequests = 0;

  // Monitor fetch requests
  const originalFetch = window.fetch;
  window.fetch = (...args) => {
    pendingFetchRequests++;
    Logger.info(`Fetch request started (${pendingFetchRequests} pending)`);

    return originalFetch.apply(this, args).finally(() => {
      pendingFetchRequests--;
      Logger.info(`Fetch request completed (${pendingFetchRequests} pending)`);
    });
  };

  // Monitor XMLHttpRequest
  const originalXHROpen = XMLHttpRequest.prototype.open;
  const originalXHRSend = XMLHttpRequest.prototype.send;

  // eslint-disable-next-line func-names
  XMLHttpRequest.prototype.open = function (
    method: string,
    url: string | URL,
    async: boolean = true,
    username?: string | null,
    password?: string | null
  ) {
    originalXHROpen.call(this, method, url, async, username, password);
    this.addEventListener(
      'loadend',
      () => {
        pendingXHRRequests--;
        Logger.info(`XHR request completed (${pendingXHRRequests} pending)`);
      },
      { once: true }
    );
  };

  // eslint-disable-next-line func-names
  XMLHttpRequest.prototype.send = function (body?: Document | XMLHttpRequestBodyInit | null) {
    pendingXHRRequests++;
    Logger.info(`XHR request started (${pendingXHRRequests} pending)`);
    originalXHRSend.call(this, body);
  };

  const hasPendingRequests = () => pendingFetchRequests > 0 || pendingXHRRequests > 0;

  const cleanup = () => {
    window.fetch = originalFetch;
    XMLHttpRequest.prototype.open = originalXHROpen;
    XMLHttpRequest.prototype.send = originalXHRSend;
  };

  return { hasPendingRequests, cleanup };
};

/**
 * Wait for all API calls to complete and pdfContentRef element to be present and loaded
 */
const waitForApiCallsAndPdfContentElement = async (document: Document, maxWaitTime: number = 60000): Promise<void> =>
  new Promise<void>(resolve => {
    const startTime = Date.now();

    // Setup network monitoring
    const { hasPendingRequests: hasPendingMonitoredRequests, cleanup } = setupNetworkMonitoring();

    const checkForApiCallsAndElement = () => {
      // Check for pending network requests using multiple methods
      const hasPendingViaPerformance = hasPendingNetworkRequests();
      const hasPendingViaMonitoring = hasPendingMonitoredRequests();

      if (hasPendingViaPerformance || hasPendingViaMonitoring) {
        Logger.info('Network requests still pending, waiting...');
        setTimeout(checkForApiCallsAndElement, TIMEOUT_MS.API_CHECK_RETRY);
        return;
      }

      // Get the query parameter from the document's URL
      const documentUrl = document.location?.href || document.defaultView?.location?.href;
      const reference = documentUrl ? new URL(documentUrl).searchParams.get(PDF_EXPORT_QUERY_PARAM_EXPORT_TARGET) : '';

      // Look for element with data-ref attribute
      const targetElement = document.querySelector(`[data-ref="${reference}"]`);

      if (targetElement) {
        Logger.info('Found target element in the DOM, checking if content is loaded...');

        // Check if element has content (not just skeleton)
        const hasContent = targetElement.children.length > 0;

        // Check for skeleton elements that might still be visible
        const skeletonElements = document.querySelectorAll(
          '[data-testid*="skeleton"], [data-testid*="loading"], ' +
            '.skeleton, .loading-skeleton, .shimmer, ' +
            '[class*="skeleton"], [class*="loading"], ' +
            '[aria-label*="loading"], [aria-label*="skeleton"]'
        );

        const hasVisibleSkeletons = Array.from(skeletonElements).some(element => {
          const style = window.getComputedStyle(element);
          return (
            style.display !== 'none' &&
            style.visibility !== 'hidden' &&
            style.opacity !== '0' &&
            (element as HTMLElement).offsetWidth > 0 &&
            (element as HTMLElement).offsetHeight > 0
          );
        });

        if (hasContent && !hasVisibleSkeletons) {
          Logger.info('Target element is loaded with content and no skeletons visible');
          Logger.info('Waiting 4 seconds for content to fully settle...');

          // Wait 4 seconds for content to fully settle after API calls are done
          setTimeout(() => {
            Logger.info('4-second delay completed, proceeding with PDF generation');
            cleanup(); // Clean up monitoring
            resolve();
          }, 4000);
          return;
        }

        if (hasVisibleSkeletons) {
          Logger.info('Skeleton elements still visible, waiting...');
        }
      }

      // Check if we've exceeded max wait time
      if (Date.now() - startTime > maxWaitTime) {
        Logger.warn('Timeout waiting for API calls and target element, proceeding anyway');
        cleanup(); // Clean up monitoring
        resolve();
        return;
      }

      // Check again in 500ms
      setTimeout(checkForApiCallsAndElement, 500);
    };

    checkForApiCallsAndElement();
  });

/**
 * Load image from public folder and convert to base64 data URL
 */
const loadImageAsDataUrl = async (imagePath: string): Promise<string> => {
  try {
    const response = await fetch(imagePath);
    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    Logger.error('Error loading image:', error);
    throw error;
  }
};

/**
 * Export multiple pages as a single PDF by navigating through URLs
 */
export const exportWithIframes = async (options: MultiplePagesExportOptions): Promise<MultiplePagesExportResult> => {
  try {
    Logger.info('PDF Export initiated with options:', options);
    const { pages, onProgress, ...pdfOptions } = options;

    if (!pages || pages.length === 0) {
      Logger.error(
        'No pages provided for multiple pages export',
        new Error('No pages provided for multiple pages export')
      );
      return { success: false, error: 'No pages provided for multiple pages export' };
    }

    Logger.info(`Starting export of ${pages.length} pages`);

    // Store canvas data for each page to create robust final PDF
    const canvases: (HTMLCanvasElement | null)[] = [];

    // Create smooth progress animator
    let globalProgressAnimator: SmoothProgressAnimator | null = null;
    if (onProgress) {
      globalProgressAnimator = new SmoothProgressAnimator((progress: number) => {
        const currentPageNum = Math.ceil((progress / 100) * pages.length) || 1;
        onProgress(currentPageNum, pages.length, Math.min(progress, PROGRESS_PERCENTAGE.CAP));
      });
    }

    try {
      for (let i = 0; i < pages.length; i++) {
        const { url } = pages[i];
        const currentPage = i + 1;
        const totalPages = pages.length;

        // Add page parameters to URL
        const urlWithPageParams = new URL(url);
        urlWithPageParams.searchParams.set('currentPage', currentPage.toString());
        urlWithPageParams.searchParams.set('totalPages', totalPages.toString());
        const finalUrl = urlWithPageParams.toString();

        Logger.info(`Processing page ${currentPage}/${totalPages}: ${finalUrl}`);

        // Calculate progress base for this page (each page contributes 1/totalPages of total progress)
        const pageProgressBase = ((currentPage - 1) / totalPages) * PROGRESS_PERCENTAGE.CONVERSION_FACTOR;
        const pageProgressRange = (1 / totalPages) * PROGRESS_PERCENTAGE.CONVERSION_FACTOR;

        // Helper function to report progress with smooth animation
        const reportProgress = (progressWithinPage: number) => {
          const totalProgress =
            pageProgressBase + (progressWithinPage * pageProgressRange);
          if (globalProgressAnimator) {
            globalProgressAnimator.setTarget(Math.min(totalProgress, PROGRESS_PERCENTAGE.CAP));
          } else {
            onProgress?.(currentPage, totalPages, Math.min(totalProgress, PROGRESS_PERCENTAGE.CAP));
          }
        };

        // Report initial progress when starting to process a page
        reportProgress(PROGRESS_MILESTONES.INITIAL);

        // Create a hidden iframe to load the page so the user doesn't see the navigation
        const iframe = document.createElement('iframe');
        iframe.src = finalUrl;
        iframe.style.position = 'fixed';
        iframe.style.zIndex = '9999';
        iframe.style.left = '-9999px';
        iframe.style.width = '2600px';

        Logger.info('Created iframe, adding to DOM');

        document.body.appendChild(iframe);

        try {
          // Wait for iframe to load
          Logger.info('Waiting for iframe to load...');
          await new Promise<void>((resolve, reject) => {
            const timeout = setTimeout(() => {
              Logger.error(`Timeout loading ${url}`, new Error(`Timeout loading ${url}`));
              reject(new Error(`Timeout loading ${url}`));
            }, TIMEOUT_MS.IFRAME_LOAD);

            iframe.onload = () => {            
              clearTimeout(timeout);
              reportProgress(PROGRESS_MILESTONES.AFTER_IFRAME_LOAD);
              resolve();
            };

            iframe.onerror = () => {
              Logger.error(`Failed to load ${url}`, new Error(`Failed to load ${url}`));
              clearTimeout(timeout);
              reject(new Error(`Failed to load ${url}`));
            };
          });

          // Get iframe document reference
          const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
          if (!iframeDoc) {
            throw new Error(`Could not access iframe content for ${url}`);
          }

          Logger.info('Waiting for API calls to complete and target element in the DOM to load...');
          reportProgress(PROGRESS_MILESTONES.BEFORE_API_WAIT);
          await waitForApiCallsAndPdfContentElement(iframeDoc);
          reportProgress(PROGRESS_MILESTONES.AFTER_API_COMPLETE);

          // Find the element to export
          const domElementRef = urlWithPageParams.searchParams.get(PDF_EXPORT_QUERY_PARAM_EXPORT_TARGET);
          const pdfContentElement = iframeDoc.querySelector(`[data-ref="${domElementRef}"]`);

          // Capture content
          reportProgress(PROGRESS_MILESTONES.CAPTURING_CONTENT);
          if (pdfContentElement) {
            const contentCanvas = await html2canvas(pdfContentElement as HTMLElement, {
              scale: 1,
              useCORS: true,
              allowTaint: true,
              logging: false,
            });
            canvases.push(contentCanvas);
          } else {
            Logger.info(`Could not find target element [${domElementRef}] in the DOM for page ${currentPage}, skipping page...`);
          }

          reportProgress(PROGRESS_MILESTONES.PAGE_COMPLETE);
        } catch (error) {
          Logger.error(`Error processing URL ${url}`, error);
          // Skip this URL and continue with the next one
          // eslint-disable-next-line no-continue
          continue;
        } finally {
          // Clean up iframe
          Logger.info('Cleaning up iframe...');
          try {
            document.body.removeChild(iframe);
          } catch (cleanupError) {
            Logger.warn('Error cleaning up iframe:', cleanupError);
          }
        }
      }
    } finally {
      // Clean up progress animator
      if (globalProgressAnimator) {
        globalProgressAnimator.cleanup();
      }
    }

    if (canvases.length === 0) {
      Logger.error('No canvas data was captured', new Error('No canvas data was captured'));
      return { success: false, error: 'No canvas data was captured' };
    }

    // Create final PDF from canvas data with proper element positioning
    Logger.info(`Creating final PDF from ${canvases.length} pages...`);

    // Initialize PDF with fixed dimensions
    const finalPdf = new JsPDF({
      format: 'letter',
      orientation: 'landscape',
      compress: true,
    });

    // Load logo image once for all pages
    let logoDataUrl: string | null = null;
    try {
      logoDataUrl = await loadImageAsDataUrl('/images/logos/pdf-report-logo.png');
    } catch {
      Logger.warn('Could not load logo image, footer will be rendered without logo');
    }

    // Render cover template on the first page
    renderCoverTemplate({
      pdf: finalPdf,
      coverTitle: pdfOptions.coverTitle,
      entityName: pdfOptions.entityName,
      dateOfIssue: pdfOptions.dateOfIssue,
    });

    for (let i = 0; i < canvases.length; i++) {
      const contentCanvas = canvases[i];

      // Add new page for each visited URL (cover is already on the first page)
      finalPdf.addPage();

      // Render page template (header and footer)
      renderPageTemplate({
        pdf: finalPdf,
        entityName: pdfOptions.entityName,
        pageTitle: pages[i].title,
        currentPage: i + 1,
        logoDataUrl,
      });

      // Add content extracted from DOM rendering in an iframe to the page
      if (contentCanvas) {
        const contentImgData = contentCanvas.toDataURL(IMAGE_SETTINGS.MIME_TYPE, IMAGE_SETTINGS.MAX_QUALITY);

        // Convert canvas dimensions from pixels to millimeters
        const canvasWidthMm = contentCanvas.width / CONVERSION_RATIOS.MM_TO_PX;
        const canvasHeightMm = contentCanvas.height / CONVERSION_RATIOS.MM_TO_PX;

        // Calculate aspect ratio
        const aspectRatio = canvasHeightMm / canvasWidthMm;

        // Fixed width of the image in millimeters
        const fixedWidthMm = IMAGE_SETTINGS.MAX_WIDTH_MM;

        // Calculate height of the image maintaining aspect ratio
        const calculatedHeightMm = fixedWidthMm * aspectRatio;

        // Determine final dimensions maintaining aspect ratio
        let contentWidthMm: number;
        let contentHeightMm: number;

        if (calculatedHeightMm > IMAGE_SETTINGS.MAX_HEIGHT_MM) {
          // If calculated height exceeds max height, use max height and recalculate width
          contentHeightMm = IMAGE_SETTINGS.MAX_HEIGHT_MM;
          contentWidthMm = contentHeightMm / aspectRatio;
        } else {
          // Otherwise, use fixed width and calculated height
          contentWidthMm = fixedWidthMm;
          contentHeightMm = calculatedHeightMm;
        }

        // Calculate content position for injection between header and footer
        const contentXPositionMm = TEMPLATE_CONSTANTS.PAGE_MARGIN_MM;
        const contentYPositionMm = TEMPLATE_CONSTANTS.HEADER_TOTAL_HEIGHT_MM;

        // Content injection as an image (screenshot of the DOM element)
        finalPdf.addImage(
          contentImgData,
          IMAGE_SETTINGS.JPEG_FORMAT,
          contentXPositionMm,
          contentYPositionMm,
          contentWidthMm,
          contentHeightMm,
          IMAGE_SETTINGS.PDF_ALIAS,
          IMAGE_SETTINGS.COMPRESSION
        );

        Logger.info(`Added content to page ${i + 1}`);
      }

      Logger.info(`Completed page ${i + 1}`);
    }

    // Download the final PDF
    const filename = pdfOptions.filename || 'multiple-pages-export';
    Logger.info(`Downloading final PDF: ${filename}.pdf`);

    try {
      finalPdf.save(`${filename}.pdf`);
      Logger.info('PDF save() method called successfully');
    } catch (saveError) {
      Logger.error('Error calling finalPdf.save():', saveError);
      // Continue anyway - the PDF was created, even if save failed
      // The error might be due to browser restrictions or popup blockers
    }

    // Wait for download to initiate with timeout protection
    // Note: We cannot reliably detect if a browser download actually started
    // due to browser security restrictions, but we can handle errors from save()
    // If the download doesn't start (e.g., blocked by browser), we still report success
    // because the PDF was generated successfully - the download failure is a browser limitation
    await new Promise<void>(resolve => {
      const startTime = Date.now();

      setTimeout(() => {
        const elapsed = Date.now() - startTime;

        // If we waited longer than the maximum expected time, log a warning
        if (elapsed > TIMEOUT_MS.DOWNLOAD_MAX_WAIT) {
          Logger.warn(
            `Wait time (${elapsed}ms) exceeded maximum (${TIMEOUT_MS.DOWNLOAD_MAX_WAIT}ms). ` +
              'Download may have been blocked by browser or popup blocker, but PDF was generated successfully.'
          );
        } else {
          Logger.info('PDF download should have initiated');
        }

        // Report completion - the PDF was generated successfully
        // Note: Even if download was blocked, the PDF creation succeeded
        if (globalProgressAnimator) {
          globalProgressAnimator.setImmediate(PROGRESS_PERCENTAGE.MAX);
        } else {
          onProgress?.(pages.length, pages.length, PROGRESS_PERCENTAGE.MAX);
        }
        resolve();
      }, TIMEOUT_MS.DOWNLOAD_DELAY);
    });

    Logger.info('Multiple pages export completed successfully');
    return {
      success: true,
      filename: `${filename}.pdf`,
      totalPages: pages.length,
      processedPages: canvases.length,
    };
  } catch (error) {
    Logger.error('Error in exportMultiplePagesAsPdf:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred during multiple pages export';
    return {
      success: false,
      error: errorMessage,
    };
  }
};
